import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Temporary AP throughput dashboard.
 *
 * Daily activity for the CFO view: emails the ingest workflow started,
 * invoice drafts created from those emails, high-confidence routing,
 * invoices the match step accepted with no person, AP invoices with a
 * completed Famous purchase match task (ap-purchase-match-famous), and
 * supplier payments marked paid (sent) that day.
 *
 * Days are calendar days in America/Los_Angeles. Counts on `days` are
 * activity for that day. `totals` for auto-accept, Famous match, and
 * payments sent are distinct entities across the window where applicable,
 * so they can be lower than the sum of the daily rows when the same
 * invoice is counted twice on different days.
 */

export const AP_THROUGHPUT_RANGES = ["7d", "30d", "90d"] as const;
export const ApThroughputRangeSchema = z.enum(AP_THROUGHPUT_RANGES);
export const DEFAULT_AP_THROUGHPUT_RANGE =
  "30d" satisfies (typeof AP_THROUGHPUT_RANGES)[number];

export const AP_THROUGHPUT_TIME_ZONE = "America/Los_Angeles";

const DaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const CountSchema = z.number().int().nonnegative();
const AmountSchema = z.string();

export const ApThroughputDaySchema = z.object({
  day: DaySchema,
  emails: CountSchema,
  drafts: CountSchema,
  draftsAmount: AmountSchema,
  confidenceMet: CountSchema,
  autoAccepted: CountSchema,
  autoAcceptedAmount: AmountSchema,
  famousPosted: CountSchema,
  famousPostedAmount: AmountSchema,
  paymentsSent: CountSchema,
  paymentsSentAmount: AmountSchema,
});

export const ApThroughputTotalsSchema = z.object({
  emails: CountSchema,
  drafts: CountSchema,
  draftsAmount: AmountSchema,
  confidenceMet: CountSchema,
  autoAccepted: CountSchema,
  autoAcceptedAmount: AmountSchema,
  famousPosted: CountSchema,
  famousPostedAmount: AmountSchema,
  paymentsSent: CountSchema,
  paymentsSentAmount: AmountSchema,
});

export const GetApThroughputInputSchema = z.object({
  range: ApThroughputRangeSchema.prefault(DEFAULT_AP_THROUGHPUT_RANGE),
});

export const GetApThroughputResponseSchema = z.object({
  timeZone: z.literal(AP_THROUGHPUT_TIME_ZONE),
  range: ApThroughputRangeSchema,
  from: DaySchema,
  to: DaySchema,
  totals: ApThroughputTotalsSchema,
  days: z.array(ApThroughputDaySchema),
});

export const AP_THROUGHPUT_DAY_SECTION_KEYS = [
  "emails",
  "drafts",
  "confidenceMet",
  "autoAccepted",
  "famousPosted",
  "paymentsSent",
] as const;

export const ApThroughputDaySectionKeySchema = z.enum(
  AP_THROUGHPUT_DAY_SECTION_KEYS,
);

/** One row in a day drill-down; `entityType` tells the UI which link to build. */
export const ApThroughputDayEntitySchema = z.object({
  entityType: z.enum(["workflow_run", "ap_invoice", "core_task", "payment"]),
  id: z.uuid(),
  title: z.string(),
  subtitle: z.string().nullable(),
  amount: AmountSchema.nullable(),
  /** When the Famous match row is a task anchored on an invoice */
  relatedInvoiceId: z.uuid().nullable(),
  /** When the row is a workflow run (email ingest or confidence) */
  relatedWorkflowId: z.uuid().nullable(),
});

export const ApThroughputDaySectionSchema = z.object({
  key: ApThroughputDaySectionKeySchema,
  total: CountSchema,
  truncated: z.boolean(),
  items: z.array(ApThroughputDayEntitySchema),
});

/** Funnel stages only — matches AP metrics UI (excludes confidenceMet). */
export const ApMetricsFunnelSectionKeySchema = z.enum([
  "emails",
  "drafts",
  "autoAccepted",
  "famousPosted",
  "paymentsSent",
]);

export const GetApThroughputDayDetailInputSchema = z.object({
  day: DaySchema,
  section: ApMetricsFunnelSectionKeySchema.optional(),
});

export const GetApThroughputDayDetailResponseSchema = z.object({
  timeZone: z.literal(AP_THROUGHPUT_TIME_ZONE),
  day: DaySchema,
  sections: z.array(ApThroughputDaySectionSchema),
});

export const apThroughputContracts = {
  get: oc
    .input(GetApThroughputInputSchema)
    .output(GetApThroughputResponseSchema)
    .route({
      method: "GET",
      summary: "Get AP throughput by day",
      description:
        "Returns daily AP activity counts and amounts over 7, 30, or 90 days: invoice emails received, drafts created, auto-accepted invoices, purchase matches posted to the ERP, and payments sent.",
    }),
  getDayDetail: oc
    .input(GetApThroughputDayDetailInputSchema)
    .output(GetApThroughputDayDetailResponseSchema)
    .route({
      method: "GET",
      summary: "List AP throughput items for a day",
      description:
        "Lists the emails, invoice drafts, auto-accepted invoices, ERP purchase matches, and sent payments behind one day of AP throughput, optionally for a single stage.",
    }),
} as const;

export type ApThroughputRange = z.infer<typeof ApThroughputRangeSchema>;
export type ApThroughputDay = z.infer<typeof ApThroughputDaySchema>;
export type ApThroughputTotals = z.infer<typeof ApThroughputTotalsSchema>;
export type GetApThroughputResponse = z.infer<
  typeof GetApThroughputResponseSchema
>;
export type ApThroughputDaySectionKey = z.infer<
  typeof ApThroughputDaySectionKeySchema
>;
export type ApThroughputDaySection = z.infer<
  typeof ApThroughputDaySectionSchema
>;
export type ApThroughputDayEntity = z.infer<typeof ApThroughputDayEntitySchema>;
export type GetApThroughputDayDetailResponse = z.infer<
  typeof GetApThroughputDayDetailResponseSchema
>;
export type ApMetricsFunnelSectionKey = z.infer<
  typeof ApMetricsFunnelSectionKeySchema
>;
